/**
 * refs.ts — React ref handling for compiled components.
 *
 * Supports callback refs (with React 19 cleanup-return pattern) and object refs.
 * No dependency on the signal/effect system.
 */
function _getCompatRenderInstance() {
    return globalThis.__REACT_COMPILER_CURRENT__?.component ?? null;
}
/**
 * applyRef — attach a ref to a DOM element.
 *
 * - Function refs: called with the element. If the callback returns a function,
 *   it is stored as _refCleanup (React 19 pattern). Otherwise the callback
 *   itself is stored as _refCallback so we can call ref(null) on unmount.
 * - Object refs ({ current }): .current is set to the element.
 */
// Deferred ref queue (BUG-RUNTIME-056). A compiled body applies its refs while
// it BUILDS its DOM, i.e. during render, not at commit. That is invisible while
// every render is committed — but the context re-render wrapper
// (render-component.ts) now renders a tree it may throw away, and firing a ref
// callback for a node that is never committed is exactly how embla's
// `setViewport`-as-ref got handed 50+ dead nodes. While a queue is installed
// applyRef records instead of invoking; the wrapper flushes it only if it
// commits the tree (which also puts ref invocation AFTER insertion, matching
// React's commit order).
let _deferQueue = null;
/** Install (or clear with null) the deferral queue; returns the previous one. */
export function setRefDeferQueue(q) {
    const prev = _deferQueue;
    _deferQueue = q;
    return prev;
}
/**
 * Apply the [el, ref, el, ref, …] pairs recorded while a queue was installed.
 *
 * FIRST occurrence per ref wins: a compiled body re-executed through the compat
 * reconciler rebuilds its DOM on every pass, but only the FIRST pass's tree is
 * committed (`_compiledSdrDom` in function-reconciler.ts), so the first node is
 * the live one. React fires each ref once per commit; this is that, deferred.
 */
export function flushDeferredRefs(q) {
    const seen = new Set();
    for (let i = 0; i < q.length; i += 2) {
        if (seen.has(q[i + 1]))
            continue;
        seen.add(q[i + 1]);
        applyRef(q[i], q[i + 1]);
    }
    q.length = 0;
}
/**
 * Park the queued refs on their fresh nodes as `_refCallback` / `_refObject`
 * WITHOUT invoking them, so a keep-alive commit that discards the fresh tree can
 * rebind them onto the live nodes it kept (`_rebindCompiledRefs`). Same
 * first-occurrence-wins rule as flushDeferredRefs.
 */
export function parkDeferredRefs(q) {
    const seen = new Set();
    for (let i = 0; i < q.length; i += 2) {
        const el = q[i];
        const ref = q[i + 1];
        if (seen.has(ref))
            continue;
        seen.add(ref);
        if (!el || el.nodeType !== 1)
            continue;
        if (typeof ref === 'function')
            el._refCallback = ref;
        else if (typeof ref === 'object')
            el._refObject = ref;
    }
    q.length = 0;
}
export function applyRef(el, ref) {
    if (!ref)
        return;
    if (_deferQueue !== null) {
        _deferQueue.push(el, ref);
        return;
    }
    // A fully-compiled body re-executed through the compat reconciler (because it
    // uses compat hooks, e.g. embla-carousel-react's useState-as-ref) builds fresh
    // DOM nodes on every render() call. React reuses host nodes so ref callbacks
    // only fire once per commit — including setState-during-render retries. Skip
    // duplicate applications within the same reconcile pass (see _compiledRefsApplied
    // in function-reconciler.ts).
    const instance = _getCompatRenderInstance();
    if (instance?.type?._isCompiled === true && instance._compiledRefsApplied) {
        if (instance._compiledRefsApplied.has(ref))
            return;
        instance._compiledRefsApplied.add(ref);
    }
    if (typeof ref === 'function') {
        const cleanup = ref(el);
        // React 19: ref callback can return a cleanup function
        if (typeof cleanup === 'function') {
            el._refCleanup = cleanup;
        }
        else {
            // Store the ref callback itself so we can call ref(null) on unmount
            el._refCallback = ref;
        }
    }
    else if (typeof ref === 'object') {
        ref.current = el;
        el._refObject = ref;
    }
}
/**
 * cleanupNodeRef — call ref cleanup when a DOM node is being removed.
 * Handles: returned cleanup functions (React 19), ref(null) calls, and object ref nulling.
 */
export function cleanupNodeRef(el) {
    if (el._refCleanup) {
        el._refCleanup();
        el._refCleanup = null;
    }
    else if (el._refCallback) {
        el._refCallback(null);
        el._refCallback = null;
    }
    else if (el._refObject) {
        el._refObject.current = null;
        el._refObject = null;
    }
}
